import { useEffect, useState } from 'react'

/**
 * The current instant, re-rendering on a tick.
 *
 * Calling `Date.now()` during render is impure -- the value changes between
 * renders for reasons React cannot see, which makes output unstable. This hook
 * turns the clock into a proper subscription instead.
 *
 * The default 60s cadence matches the now-marker in 03-ui-spec.md section 2.7.
 * Pass a shorter interval only where it genuinely matters; every tick is a
 * re-render, and on the timeline that is expensive.
 */
export function useNow(intervalMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    // Align the first tick to the next boundary so the marker moves when the
    // minute actually changes, rather than at an arbitrary offset from mount.
    const delay = intervalMs - (Date.now() % intervalMs)

    let interval: ReturnType<typeof setInterval> | undefined

    const timeout = setTimeout(() => {
      setNow(Date.now())
      interval = setInterval(() => setNow(Date.now()), intervalMs)
    }, delay)

    // The clock also needs correcting when the app returns from the background,
    // where timers are throttled or suspended entirely.
    const onVisible = () => {
      if (document.visibilityState === 'visible') setNow(Date.now())
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      clearTimeout(timeout)
      if (interval !== undefined) clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [intervalMs])

  return now
}
